import { useApi } from '@/api';
import { VaultName, Link, TableSkeleton } from '@/components';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';
import { tokensMapping } from '@/utils/tokens';
import {
  CryptoIcon,
  CurrencyValue,
  Flex,
  PercentageValue,
  RelativeDateTime,
  Box,
  DataTable
} from '@blockanalitica/ui';

export default function CdpsTable({ pagination, sorting, appliedFilter }) {
  const [data, error] = useApi({
    url: `/groups/core/ilks/cdps/`,
    queryParams: {
      p: pagination.currentPage,
      p_size: pagination.pageSize,
      order: sorting.currentSorting,
      days_ago: 1,
      search: appliedFilter
    }
  });

  if (error) {
    throw error;
  }

  if (data) {
    const columns: TableColumn[] = [
      {
        header: 'CDP',
        getCell: (row) => <VaultName cdp={row.cdp} urn={row.urn} />,
        sortingField: 'cdp'
      },

      {
        header: (
          <Flex
            display="flex"
            flexDirection="row"
            alignItems="center"
            gap="3xs">
            Debt
            <CryptoIcon
              name={tokensMapping('DAI').symbol}
              address={tokensMapping('DAI').address}
              size="0"
            />
          </Flex>
        ),
        getCell: (row) => <CurrencyValue value={row.debt} />,

        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'debt'
      },
      {
        header: `Change (1D)`,
        getCell: (row) => (
          <CurrencyValue
            value={row[`debt_change_1d`]}
            options={{ trend: true, iconSize: '0' }}
          />
        ),
        getCellBottom: (row) => (
          <Flex gap="3xs" color="muted" fontSize="-1">
            <PercentageValue
              value={row[`debt_change_1d`] / row.debt}
              options={{
                nullable: '-',
                iconSize: '-1'
              }}
            />
          </Flex>
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: `debt_change_1d`
      },
      {
        header: `Collateral`,
        getCell: (row) => (
          <CurrencyValue
            value={row.ink}
            options={{
              currency: {
                name: row.collateral_sumbol,
                address: row.collateral_address
              },
              iconSize: '0'
            }}
          />
        ),
        getCellBottom: (row) => (
          <Flex gap="3xs" color="muted" fontSize="-1">
            <CurrencyValue
              value={row[`ink_change_1d`]}
              options={{
                trend: true,
                nullable: null,
                iconSize: '-1'
              }}
            />
          </Flex>
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'ink'
      },
      {
        header: 'Liquidation Price',
        getCell: (row) => (
          <CurrencyValue
            value={row.liquidation_price}
            options={{
              notation: 'standard',
              currency: 'USD'
            }}
          />
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'liquidation_price'
      },
      {
        header: 'CR',
        getCell: (row) => (
          <PercentageValue value={row.collateralization_ratio} />
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'collateralization_ratio'
      },
      {
        header: 'Time',
        getCell: (row) => (
          <Box color="muted">
            <RelativeDateTime value={row.datetime} />
          </Box>
        ),
        sortingField: 'datetime',
        cellAlign: 'flex-end',
        headerAlign: 'flex-end'
      }
    ];

    return (
      <DataTable
        rows={data['results']}
        columns={columns}
        getKey={(row) => row.urn + row.ilk}
        rowLinkProps={{
          to: (row) => `/collateral/core/${row.ilk}/cdps/${row.urn}/`,
          // @ts-ignore
          linkComponent: Link
        }}
        sorting={sorting}
        pagination={{
          ...pagination,
          totalRecords: data.count
        }}
      />
    );
  }
  return <TableSkeleton />;
}
